import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Play, Pause, SkipForward, Plus, Check, Volume2, VolumeX, Languages, Flame, Timer as TimerIcon,
  Clock, Infinity as InfinityIcon, Waves, PartyPopper, Music2, Maximize2,
} from 'lucide-react';
import { ExerciseMedia } from './ExerciseLibraryUI';
import { ExerciseDetailExpand } from './SessionRunner';
import { CARDIO_VOICES, loadCardioAudioManifest, playCardioVoice, unlockCardioAudio } from './cardioAudio';
import { Confetti } from './RestTimer';
import { SOUND_THEMES, playCountdownTick, playGo, unlockAudio } from './soundThemes';

const METHOD_ICONS = { hiit: Flame, tabata: TimerIcon, emom: Clock, amrap: InfinityIcon, intervals: Waves };
const ENCOURAGEMENT_KEYS = ['encourage_1', 'encourage_2', 'encourage_3', 'encourage_4', 'encourage_5', 'encourage_6', 'encourage_7', 'encourage_8'];
const randomEncouragement = () => ENCOURAGEMENT_KEYS[Math.floor(Math.random() * ENCOURAGEMENT_KEYS.length)];
const PHASE_LABELS_FR = { warmup: 'ÉCHAUFFEMENT', work: 'EFFORT', rest: 'REPOS', cooldown: 'RETOUR AU CALME' };
const PHASE_LABELS_EN = { warmup: 'WARM-UP', work: 'WORK', rest: 'REST', cooldown: 'COOL DOWN' };
const PHASE_COLORS = { warmup: 'text-sky-400', work: 'text-orange-400', rest: 'text-emerald-400', cooldown: 'text-violet-400' };

const METHOD_LABELS = {
  hiit: 'HIIT', tabata: 'Tabata', emom: 'EMOM', amrap: 'AMRAP', intervals: { fr: 'Intervalles', en: 'Intervals' },
};
const methodLabel = (method, en) => {
  const l = METHOD_LABELS[method];
  return typeof l === 'string' ? l : (l ? (en ? l.en : l.fr) : method);
};

const fmt = (s) => {
  const v = Math.max(0, Math.round(s));
  const m = Math.floor(v / 60);
  const sec = v % 60;
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}`;
};

const buildPhases = (session) => {
  const phases = [];
  if (session.warmup_seconds > 0) phases.push({ type: 'warmup', duration: session.warmup_seconds });
  session.blocks.forEach((block, blockIndex) => {
    const isAmrap = block.method === 'amrap';
    const totalRounds = isAmrap ? 1 : block.rounds;
    for (let round = 1; round <= totalRounds; round++) {
      const exercise = block.exercises[(round - 1) % block.exercises.length];
      phases.push({
        type: 'work', blockIndex, round, totalRounds, method: block.method, isAmrap,
        duration: isAmrap ? (block.time_cap_seconds || block.work_seconds) : block.work_seconds,
        exercise,
      });
      if (!isAmrap && block.rest_seconds > 0 && round < totalRounds) {
        phases.push({ type: 'rest', blockIndex, duration: block.rest_seconds });
      }
    }
    if (blockIndex < session.blocks.length - 1) {
      phases.push({ type: 'rest', blockIndex, transition: true, duration: Math.max(block.rest_seconds, 20) || 30 });
    }
  });
  if (session.cooldown_seconds > 0) phases.push({ type: 'cooldown', duration: session.cooldown_seconds });
  return phases;
};

/** Tracking dédié Cardio (HIIT/Tabata/EMOM/AMRAP/Intervalles) — minuteur audio-visuel natif
 * (voix pré-enregistrée 3-2-1-GO + repos, choix masculin/féminin) + vidéo exercice affichée
 * pendant l'effort. Distinct du SessionRunner musculation. */
export const CardioSessionRunner = ({ en, session, authFetch, onBack, onSubmit }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [voice, setVoice] = useState(() => localStorage.getItem('cardio_voice_pref') || 'male');
  const [voiceLang, setVoiceLang] = useState(() => localStorage.getItem('cardio_voice_lang_pref') || 'fr');
  const [voiceEnabled, setVoiceEnabled] = useState(() => localStorage.getItem('cardio_voice_enabled_pref') !== 'false');
  const [audioReady, setAudioReady] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(-1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [roundsCompleted, setRoundsCompleted] = useState({});
  const [amrapCount, setAmrapCount] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [soundTheme, setSoundTheme] = useState(() => localStorage.getItem('bu_sound_theme') || 'classique');
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [detailEx, setDetailEx] = useState(null);

  const phases = useMemo(() => buildPhases(session), [session]);
  const PHASE_LABELS = en ? PHASE_LABELS_EN : PHASE_LABELS_FR;

  // Horodatages réels (pas de simple décrément) : le décompte de phase et le temps total écoulé
  // restent exacts même si le `setTimeout` est throttlé/gelé en arrière-plan (onglet caché,
  // changement d'app, écran verrouillé) — jamais de blocage du minuteur.
  const phaseEndTimeRef = useRef(null);
  const sessionStartRef = useRef(null);
  const pausedAtRef = useRef(null);
  const pausedMsRef = useRef(0);
  const lastAnnouncedSecRef = useRef(null);
  const advanceRef = useRef(null);

  useEffect(() => { loadCardioAudioManifest(authFetch).then(() => setAudioReady(true)); }, [authFetch]);
  useEffect(() => { localStorage.setItem('cardio_voice_pref', voice); }, [voice]);
  useEffect(() => { localStorage.setItem('cardio_voice_lang_pref', voiceLang); }, [voiceLang]);
  useEffect(() => { localStorage.setItem('cardio_voice_enabled_pref', String(voiceEnabled)); }, [voiceEnabled]);
  const changeSoundTheme = (t) => { setSoundTheme(t); localStorage.setItem('bu_sound_theme', t); };
  const speak = useCallback((key) => { if (voiceEnabled) playCardioVoice(voice, key, voiceLang === 'en'); }, [voice, voiceLang, voiceEnabled]);

  const currentPhase = phaseIdx >= 0 ? phases[phaseIdx] : null;

  useEffect(() => {
    if (currentPhase?.type === 'work' && currentPhase.isAmrap) setAmrapCount(0);
  }, [phaseIdx, currentPhase]);

  const advance = useCallback(() => {
    const done = phases[phaseIdx];
    if (done?.type === 'work') {
      setRoundsCompleted((prev) => ({ ...prev, [done.blockIndex]: done.isAmrap ? amrapCount : done.round }));
    }
    const nextIdx = phaseIdx + 1;
    if (nextIdx >= phases.length) {
      setFinished(true); setRunning(false);
      speak('finished');
      playGo(soundTheme, true);
      return;
    }
    const next = phases[nextIdx];
    phaseEndTimeRef.current = Date.now() + next.duration * 1000;
    lastAnnouncedSecRef.current = null;
    setPhaseIdx(nextIdx);
    setTimeLeft(next.duration);
    if (next.type === 'work') { speak('go'); playGo(soundTheme, true); }
    else if (next.type === 'rest') speak(next.transition ? 'recovery' : 'rest');
    else if (next.type === 'warmup') speak('warmup');
    else if (next.type === 'cooldown') speak('cooldown');
  }, [phaseIdx, phases, amrapCount, soundTheme, speak]);

  useEffect(() => { advanceRef.current = advance; }, [advance]);

  // Boucle auto-suffisante (indépendante de l'état `timeLeft`) : chaque tick recalcule le temps
  // restant de la phase et le temps total écoulé à partir des horodatages réels, jamais d'un
  // simple décrément — le minuteur ne se bloque donc jamais en arrière-plan.
  useEffect(() => {
    if (!running || finished || phaseIdx < 0) return;
    let timeoutId;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((phaseEndTimeRef.current - Date.now()) / 1000));
      const elapsed = Math.max(0, Math.floor((Date.now() - sessionStartRef.current - pausedMsRef.current) / 1000));
      setTimeLeft(remaining);
      setElapsedSeconds(elapsed);

      if (remaining !== lastAnnouncedSecRef.current) {
        lastAnnouncedSecRef.current = remaining;
        if (remaining === 3) { speak('three'); playCountdownTick(soundTheme, 3, true); }
        else if (remaining === 2) { speak('two'); playCountdownTick(soundTheme, 2, true); }
        else if (remaining === 1) { speak('one'); playCountdownTick(soundTheme, 1, true); }
        else if (currentPhase?.type === 'work' && currentPhase.duration >= 25 && remaining === Math.floor(currentPhase.duration / 2)) {
          speak(randomEncouragement());
        }
      }
      if (remaining <= 0) { advanceRef.current(); return; }
      timeoutId = setTimeout(tick, 1000);
    };
    tick();
    return () => clearTimeout(timeoutId);
  }, [running, finished, phaseIdx, soundTheme, speak, currentPhase]);

  // Resynchronisation immédiate dès que l'onglet/app redevient visible (le setTimeout peut avoir
  // été entièrement suspendu pendant l'arrière-plan sur mobile).
  useEffect(() => {
    const resync = () => {
      if (document.visibilityState !== 'visible' || !running || finished || phaseIdx < 0) return;
      const remaining = Math.max(0, Math.ceil((phaseEndTimeRef.current - Date.now()) / 1000));
      const elapsed = Math.max(0, Math.floor((Date.now() - sessionStartRef.current - pausedMsRef.current) / 1000));
      setTimeLeft(remaining);
      setElapsedSeconds(elapsed);
      if (remaining <= 0) advanceRef.current();
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [running, finished, phaseIdx]);

  const pauseTimer = () => {
    if (pausedAtRef.current == null) pausedAtRef.current = Date.now();
    setRunning(false);
  };

  const togglePause = () => {
    if (running) { pauseTimer(); return; }
    if (pausedAtRef.current != null) {
      pausedMsRef.current += Date.now() - pausedAtRef.current;
      phaseEndTimeRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
    setRunning(true);
  };

  const start = () => {
    if (voiceEnabled) unlockCardioAudio(voice, voiceLang === 'en');
    unlockAudio();
    sessionStartRef.current = Date.now();
    pausedMsRef.current = 0;
    pausedAtRef.current = null;
    phaseEndTimeRef.current = Date.now() + phases[0].duration * 1000;
    lastAnnouncedSecRef.current = null;
    setPhaseIdx(0);
    setTimeLeft(phases[0].duration);
    setRunning(true);
    if (phases[0].type === 'work') { speak('go'); playGo(soundTheme, true); }
    else if (phases[0].type === 'warmup') speak('warmup');
  };

  const submitLog = () => {
    setSubmitting(true);
    const blocks = session.blocks.map((b, idx) => ({ block_index: idx, rounds_completed: roundsCompleted[idx] ?? 0, method: b.method }));
    const calories_estimate = Math.round((elapsedSeconds / 60) * 9);
    Promise.resolve(onSubmit({ session_label: session.session_label, duration_seconds: elapsedSeconds, blocks, calories_estimate }))
      .finally(() => setSubmitting(false));
  };

  if (finished) {
    return (
      <div className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="cardio-runner-finished">
        <Confetti />
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-3xl p-8 max-w-sm w-full text-center">
          <PartyPopper className="w-14 h-14 text-orange-400 mx-auto mb-4" />
          <h3 className="text-2xl font-black text-white mb-2">{L('Séance terminée !', 'Session complete!')}</h3>
          <p className="text-zinc-400 text-sm mb-4">{L('Bravo, vous avez tenu la distance.', 'Great job, you made it through.')}</p>
          <div className="bg-black/40 rounded-xl p-4 mb-5">
            <p className="text-3xl font-black text-orange-400">{fmt(elapsedSeconds)}</p>
            <p className="text-xs text-zinc-500">{L('Durée totale', 'Total duration')}</p>
          </div>
          <button onClick={submitLog} disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-105 transition-transform disabled:opacity-60"
            data-testid="cardio-runner-submit-btn">
            <Check className="w-4 h-4" /> {L('Terminer', 'Finish')}
          </button>
        </motion.div>
      </div>
    );
  }

  if (phaseIdx < 0) {
    return (
      <div className="space-y-5" data-testid="cardio-runner-ready-screen">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="text-zinc-400 hover:text-white" data-testid="cardio-runner-back-btn"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-1.5 flex-wrap justify-end relative">
            {CARDIO_VOICES.map((v) => (
              <button key={v.value} onClick={() => setVoice(v.value)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${voice === v.value ? 'bg-orange-500 text-white border-transparent' : 'bg-zinc-800/60 text-zinc-400 border-white/10'}`}
                data-testid={`cardio-voice-select-${v.value}`}>
                <Volume2 className="w-3 h-3" /> {v.label}
              </button>
            ))}
            <button onClick={() => setVoiceLang((l) => (l === 'fr' ? 'en' : 'fr'))}
              title={L('Langue de la voix', 'Voice language')}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-white/10 bg-zinc-800/60 text-zinc-400 hover:text-white transition-colors"
              data-testid="cardio-voice-lang-toggle">
              <Languages className="w-3 h-3" /> {voiceLang === 'fr' ? 'FR' : 'EN'}
            </button>
            <button onClick={() => setVoiceEnabled((v) => !v)}
              title={voiceEnabled ? L('Désactiver la voix', 'Disable voice') : L('Activer la voix', 'Enable voice')}
              className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${voiceEnabled ? 'bg-zinc-800/60 text-zinc-400 border-white/10 hover:text-white' : 'bg-red-500/15 text-red-400 border-red-500/25'}`}
              data-testid="cardio-voice-enabled-toggle">
              {voiceEnabled ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />} {voiceEnabled ? L('Voix ON', 'Voice ON') : L('Voix OFF', 'Voice OFF')}
            </button>
            <button onClick={() => setShowThemePicker((v) => !v)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-white/10 bg-zinc-800/60 text-zinc-400 hover:text-white transition-colors"
              data-testid="cardio-sound-theme-btn">
              <Music2 className="w-3 h-3" /> {SOUND_THEMES.find((t) => t.value === soundTheme)?.label}
            </button>
            {showThemePicker && (
              <div className="absolute top-9 right-0 z-20 bg-black/80 backdrop-blur-2xl border border-white/10 rounded-xl p-2 space-y-1 w-44" data-testid="cardio-sound-theme-picker">
                {SOUND_THEMES.map((t) => (
                  <button key={t.value} onClick={() => { changeSoundTheme(t.value); setShowThemePicker(false); }}
                    className={`block w-full text-left px-3 py-1.5 rounded-lg text-xs font-semibold ${soundTheme === t.value ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:bg-white/5'}`}
                    data-testid={`cardio-sound-theme-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <h2 className="text-xl font-black text-white">{session.session_label}</h2>

        <div className="space-y-3" data-testid="cardio-runner-block-summary-list">
          {session.warmup_seconds > 0 && (
            <div className="flex items-center gap-3 rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 px-4 py-3">
              <TimerIcon className="w-5 h-5 text-sky-400 shrink-0" />
              <span className="text-white font-bold text-sm flex-1">{L('Échauffement', 'Warm-up')}</span>
              <span className="text-zinc-500 text-xs font-semibold">{fmt(session.warmup_seconds)}</span>
            </div>
          )}
          {session.blocks.map((block, blockIndex) => {
            const isAmrap = block.method === 'amrap';
            const totalRounds = isAmrap ? 1 : block.rounds;
            const uniqueNames = [...new Set(block.exercises.map((e) => e?.name).filter(Boolean))];
            const Icon = METHOD_ICONS[block.method] || Flame;
            return (
              <motion.div
                key={blockIndex} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: blockIndex * 0.05 }}
                className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4 flex flex-col gap-3"
                data-testid={`cardio-runner-block-card-${blockIndex}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-2 text-white font-black text-sm">
                    <Icon className="w-4.5 h-4.5 text-orange-400" /> {methodLabel(block.method, en)} <span className="text-zinc-500 font-semibold">· {L('Bloc', 'Block')} {blockIndex + 1}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide bg-primary/20 text-primary border border-primary/30" data-testid={`cardio-runner-block-badge-${blockIndex}`}>
                    {isAmrap ? `${fmt(block.time_cap_seconds || block.work_seconds)} AMRAP` : `x${totalRounds} ${L('ROUNDS', 'ROUNDS')}`}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {uniqueNames.map((name, i) => (
                    <React.Fragment key={name + i}>
                      <span className="text-sm font-semibold text-white/85 bg-black/30 border border-white/10 rounded-lg px-2.5 py-1">{name}</span>
                      {i < uniqueNames.length - 1 && <span className="text-zinc-600 text-xs">→</span>}
                    </React.Fragment>
                  ))}
                </div>
                <p className="text-xs text-zinc-500">
                  {L('Effort', 'Work')} {fmt(block.work_seconds)}{!isAmrap && block.rest_seconds > 0 ? ` · ${L('Repos', 'Rest')} ${fmt(block.rest_seconds)}` : ''}
                </p>
              </motion.div>
            );
          })}
          {session.cooldown_seconds > 0 && (
            <div className="flex items-center gap-3 rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 px-4 py-3">
              <TimerIcon className="w-5 h-5 text-violet-400 shrink-0" />
              <span className="text-white font-bold text-sm flex-1">{L('Retour au calme', 'Cool down')}</span>
              <span className="text-zinc-500 text-xs font-semibold">{fmt(session.cooldown_seconds)}</span>
            </div>
          )}
        </div>

        <button onClick={start} disabled={!audioReady}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-black text-lg text-white bg-gradient-to-r from-orange-500 to-red-500 hover:scale-[1.02] transition-transform disabled:opacity-60"
          data-testid="cardio-runner-start-btn">
          <Play className="w-5 h-5" /> {L('Démarrer', 'Start')}
        </button>
      </div>
    );
  }

  const totalDuration = currentPhase.type === 'work' && currentPhase.isAmrap ? (session.blocks[currentPhase.blockIndex].time_cap_seconds || currentPhase.duration) : currentPhase.duration;
  const progressPct = Math.max(0, Math.min(100, (timeLeft / totalDuration) * 100));
  const globalPct = Math.round((phaseIdx / phases.length) * 100);

  const PHASE_HEX = { warmup: '#3B82F6', work: '#F97316', rest: '#22C55E', cooldown: '#A855F7' };
  const phaseHex = PHASE_HEX[currentPhase.type];

  return (
    <div className="fixed inset-0 z-[110] bg-black flex flex-col overflow-hidden" data-testid="cardio-runner-active">
      {/* z-0 : fond plein écran (photo de l'exercice en cours, floutée + assombrie pour rester
          100% lisible) — la texture donne l'énergie, jamais au détriment de la lisibilité. */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          {currentPhase.type === 'work' && currentPhase.exercise?.image_url ? (
            <motion.img
              key={currentPhase.exercise.image_url}
              src={`${process.env.REACT_APP_BACKEND_URL}${currentPhase.exercise.image_url}`}
              alt="" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
              className="w-full h-full object-cover scale-110 blur-2xl"
            />
          ) : (
            <motion.div key="blank" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full h-full bg-black" />
          )}
        </AnimatePresence>
        <div className="absolute inset-0 bg-black/75" />
      </div>

      {/* z-10 : halo ambiant pulsant, couleur de la phase en cours */}
      <motion.div
        className="absolute z-10 top-1/2 left-1/2 w-[26rem] h-[26rem] rounded-full mix-blend-screen pointer-events-none"
        style={{ filter: 'blur(110px)', translateX: '-50%', translateY: '-50%' }}
        animate={{ backgroundColor: phaseHex, opacity: currentPhase.type === 'work' ? [0.45, 0.75, 0.45] : 0.4 }}
        transition={currentPhase.type === 'work' ? { opacity: { duration: 1.1, repeat: Infinity, ease: 'easeInOut' }, backgroundColor: { duration: 0.8 } } : { backgroundColor: { duration: 0.8 } }}
      />

      {/* z-30 : barre de progression globale + top bar */}
      <div className="relative z-30 h-1 bg-white/10"><div className="h-full transition-[width] duration-500" style={{ width: `${globalPct}%`, backgroundColor: phaseHex, boxShadow: `0 0 12px ${phaseHex}` }} /></div>
      <div className="relative z-30 flex items-center justify-between px-5 py-3">
        <button onClick={onBack} className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-xl border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white transition-colors" data-testid="cardio-runner-exit-btn"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setVoiceEnabled((v) => !v)} className="w-9 h-9 rounded-full bg-black/50 backdrop-blur-xl border border-white/10 flex items-center justify-center text-zinc-300 hover:text-white transition-colors" data-testid="cardio-runner-voice-mute-btn">
            {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-red-400" />}
          </button>
          <button onClick={() => setVoiceLang((l) => (l === 'fr' ? 'en' : 'fr'))} className="inline-flex items-center gap-1 px-2.5 py-2 rounded-full bg-black/50 backdrop-blur-xl border border-white/10 text-xs font-semibold text-zinc-300 hover:text-white transition-colors" data-testid="cardio-runner-voice-lang-btn">
            <Languages className="w-3.5 h-3.5" /> {voiceLang === 'fr' ? 'FR' : 'EN'}
          </button>
          <button onClick={() => setVoice((v) => (v === 'male' ? 'female' : 'male'))} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/50 backdrop-blur-xl border border-white/10 text-xs font-semibold text-zinc-300 hover:text-white transition-colors" data-testid="cardio-runner-voice-toggle">
            <Volume2 className="w-3.5 h-3.5" /> {CARDIO_VOICES.find((v) => v.value === voice)?.label}
          </button>
        </div>
      </div>

      {/* z-20 : contenu principal */}
      <div className="relative z-20 flex-1 flex flex-col items-center justify-center px-4 gap-2 py-2 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.p
            key={`label-${phaseIdx}`} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            className="text-lg sm:text-xl font-black uppercase tracking-[0.2em] drop-shadow-lg" style={{ color: phaseHex }}
            data-testid="cardio-runner-phase-label"
          >
            {PHASE_LABELS[currentPhase.type]}
          </motion.p>
        </AnimatePresence>

        {currentPhase.type === 'work' && (
          <motion.div
            key={`media-${phaseIdx}`} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-[22rem] sm:max-w-sm aspect-video rounded-3xl overflow-hidden border-2 shadow-2xl" style={{ borderColor: phaseHex, boxShadow: `0 0 30px ${phaseHex}55` }}
            data-testid="cardio-runner-exercise-media"
          >
            <ExerciseMedia exercise={currentPhase.exercise} alt={currentPhase.exercise?.name} autoPlayVideo />
          </motion.div>
        )}
        {currentPhase.type === 'work' && <p className="text-white font-bold text-base sm:text-lg text-center drop-shadow" data-testid="cardio-runner-exercise-name">{currentPhase.exercise?.name}</p>}
        {currentPhase.type === 'work' && currentPhase.exercise?.execution && (
          <p className="text-white/70 text-xs text-center max-w-xs line-clamp-1" data-testid={`cardio-exercise-mini-summary-${phaseIdx}`}>
            {currentPhase.exercise.execution.length > 90 ? `${currentPhase.exercise.execution.slice(0, 90).trim()}…` : currentPhase.exercise.execution}
          </p>
        )}
        {currentPhase.type === 'work' && (currentPhase.exercise?.image_url || currentPhase.exercise?.execution) && (
          <button onClick={() => { pauseTimer(); setDetailEx(currentPhase.exercise); }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black text-white bg-gradient-to-r from-primary to-fuchsia-500 shadow-[0_0_16px_-2px_rgba(217,70,239,0.7)] hover:scale-105 active:scale-95 transition-transform animate-pulse-slow"
            data-testid="cardio-runner-view-more-btn">
            <Maximize2 className="w-3 h-3" /> {L('Voir + (vidéo, conseils pro)', 'See more (video, pro tips)')}
          </button>
        )}
        {currentPhase.type === 'work' && !currentPhase.isAmrap && (
          <p className="text-white/80 text-xs sm:text-sm font-bold uppercase tracking-wide">{L('Tour', 'Round')} {currentPhase.round}/{currentPhase.totalRounds}</p>
        )}

        <div className="relative w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center shrink-0">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
            <circle cx="50" cy="50" r="44" fill="none" stroke={phaseHex} strokeWidth="6"
              style={{ filter: `drop-shadow(0 0 8px ${phaseHex})`, transition: 'stroke-dashoffset 1s linear' }}
              strokeDasharray={`${2 * Math.PI * 44}`} strokeDashoffset={`${2 * Math.PI * 44 * (1 - progressPct / 100)}`} strokeLinecap="round" />
          </svg>
          <AnimatePresence mode="popLayout">
            <motion.span
              key={`t-${phaseIdx}`} initial={{ scale: 1.3, opacity: 0.5 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', damping: 15 }}
              className="text-4xl sm:text-5xl font-black text-white tabular-nums drop-shadow-2xl" data-testid="cardio-runner-timer-value"
            >
              {fmt(timeLeft)}
            </motion.span>
          </AnimatePresence>
        </div>

        {currentPhase.type === 'work' && currentPhase.isAmrap && (
          <button onClick={() => setAmrapCount((c) => c + 1)}
            className="w-full max-w-xs inline-flex items-center justify-center gap-2 py-4 rounded-3xl font-black text-lg text-white bg-gradient-to-r from-orange-500 to-red-500 active:scale-95 transition-transform shadow-[0_0_25px_rgba(249,115,22,0.45)]"
            data-testid="cardio-runner-amrap-plus-btn">
            <Plus className="w-5 h-5" /> {L('Tour', 'Round')} {amrapCount}
          </button>
        )}
      </div>

      <div className="relative z-30 flex items-center justify-center gap-4 px-5 pb-6 pt-1 shrink-0">
        <button onClick={togglePause}
          className="w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform active:scale-90 shadow-[0_0_25px_rgba(255,23,68,0.35)]"
          style={{ backgroundColor: '#FF1744' }}
          data-testid="cardio-runner-pause-btn">
          {running ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
        </button>
        <button onClick={advance}
          className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-black/50 backdrop-blur-xl border border-white/15 text-zinc-300 hover:text-white text-sm font-bold transition-colors"
          data-testid="cardio-runner-skip-btn">
          <SkipForward className="w-4 h-4" /> {L('Passer', 'Skip')}
        </button>
      </div>
      <ExerciseDetailExpand ex={detailEx} authFetch={authFetch} onClose={() => setDetailEx(null)} L={L} />
    </div>
  );
};

export default CardioSessionRunner;
